import React, { useState, useContext } from "react"
import { Container, Box, Dialog, IconButton, Button as MUIButton } from "@mui/material"
import { makeStyles } from "makeStyles"
import Icon from "@mdi/react"
import { mdiClose, mdiCartPlus } from "@mdi/js"
import { useSnackbar } from "notistack"

import Button from "components/Buttons/Button"
import Price from "components/Price"
import ChangeAmountButton from "components/Buttons/ChangeAmountButton"
import Spinner from "components/Spinner"
import LazyLoadedImage from "components/LazyLoadedImage"

import DesktopLogo from "svg/logo-digitalzylinder-shop.svg"

import AppContext from "@secureo/common/context/AppContext/AppContext"
import { distributionChannel, custom } from "@secureo/common/reducers/productDetailPageReducer"

import useUpSellProducts, {
	UpSellSellProduct as UpSellSellProductInterface,
} from "@secureo/common/hooks/data/useUpSellProducts"

import { addLineItemsToCart } from "@secureo/common/context/AppContext/actions"
import getImageWithSizeSuffixes from "@secureo/common/utils/commerceTools/formatters/getImageWithSizeSuffixes"
import { centsToEuros } from "@secureo/common/utils/forex/convertCurrencyFraction"
import { trackAddToCart /*trackAddToCartGA4*/ } from "@secureo/common/utils/tracking/cart"
import { getLineItemTaxRate } from "@secureo/common/utils/tax"
import { netToGross } from "@secureo/common/utils/tax"
import { trackProductHitImpression } from "@secureo/common/utils/tracking/productHit"

import { MyLineItemDraft } from "@secureo/common/typings/generated/commercetools-graphql"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import { EnhancedEcommerceCartProductFieldObject } from "@secureo/common/typings/EnhancedEcommerce"
// import wait from "@secureo/common/utils/wait"
// import { GA4Item } from "@secureo/common/typings/GoogleAnalytics4Ecommerce"

const useStyles = makeStyles()({
	desktopUpSellPosition: {
		position: "absolute",
		zIndex: 0,
		top: 0,
		left: 0,
		width: "73%",
		height: "100%",
		background: "white",
		display: "block",
		"@media only screen and (min-width: 1200px) and (max-width: 1400px)": {
			width: "60%",
		},
		"@media only screen and (min-width: 860px) and (max-width: 1200px)": {
			width: "65%",
		},
		"@media only screen and (max-width: 1060px)": {
			display: "none",
		},
	},
	mobileUpSellPosition: {
		display: "none",
		"@media only screen and (max-width: 1060px)": {
			display: "block",
		},
	},
})

const UpSellProduct = ({
	upSellProduct,
	addUpSellProductToCart,
	countryCode,
	productSku,
	position,
}: UpSellProductProps) => {
	const {
		image,
		name,
		pricesByCountryCode,
		shortDescription = "",
		manufacturer,
		shippingOriginationCountryCodesByDestinationCountryCode,
	} = upSellProduct

	const { enqueueSnackbar } = useSnackbar()
	const [quantity, setQuantity] = useState(0)
	const [isLoading, setIsLoading] = useState(false)

	const singleNetCents = pricesByCountryCode?.[countryCode] ?? 0
	const shippingOriginationCountryCode =
		shippingOriginationCountryCodesByDestinationCountryCode?.[countryCode] ?? "DE"
	const { taxRate } = getLineItemTaxRate(shippingOriginationCountryCode, countryCode)
	const singleGrossCents = Math.round(netToGross(singleNetCents, taxRate))

	const addToCart = async () => {
		try {
			setIsLoading(true)
			await addUpSellProductToCart(upSellProduct, quantity, singleGrossCents)
			enqueueSnackbar("Zum Warenkorb hinzugefügt", {
				variant: "success",
			})
		} catch (err) {
			console.error(err)
			enqueueSnackbar("Etwas ist schiefgelaufen", {
				variant: "error",
			})
		} finally {
			setIsLoading(false)
		}
	}

	const { medium } = getImageWithSizeSuffixes(image)

	const onIntersection = () => {
		trackProductHitImpression(
			name,
			singleGrossCents,
			manufacturer,
			`${productSku} - Cross Sells`,
			`${productSku} - Cross Sells`,
			position,
		)
	}

	return (
		<Box
			mb={2}
			p={1}
			key={name}
			style={{
				display: "flex",
				background: "#EDEFF0",
				alignItems: "flex-start",
				borderRadius: 9,
			}}
		>
			<Box mr={2} p={1} style={{ background: "#EDEFF0" }}>
				<LazyLoadedImage
					alt={name}
					title={name}
					src={medium}
					style={{
						height: 60,
						width: 70,
						objectFit: "contain",
						mixBlendMode: "multiply",
					}}
					onIntersection={onIntersection}
				/>
			</Box>
			<Box p={1} style={{ color: "#707070", width: "100%" }}>
				<strong>{name}</strong>
				<Box mt={1}>
					<div dangerouslySetInnerHTML={{ __html: shortDescription }} />
				</Box>
				<Box
					mt={1}
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<div style={{ color: "#263739" }}>
						<strong>
							<Price priceInEuroCents={singleGrossCents} />
						</strong>
						<span style={{ fontSize: 13, marginLeft: 4 }}>inkl. MwSt.</span>
					</div>
					<div style={{ display: "flex", alignItems: "center" }}>
						<ChangeAmountButton
							quantity={quantity}
							onChange={setQuantity}
							minQuantity={1}
							disabled={isLoading}
						/>
						<IconButton
							color="secondary"
							style={{ background: "#4691CE", padding: 7 }}
							onClick={addToCart}
							disabled={isLoading || quantity < 1}
							size="large"
						>
							<Icon path={mdiCartPlus} size={1} color="white" />
						</IconButton>
					</div>
				</Box>
			</Box>
		</Box>
	)
}

interface UpSellProductProps {
	upSellProduct: UpSellSellProductInterface
	addUpSellProductToCart: (
		upSellProduct: UpSellSellProductInterface,
		quantity: number,
		singleGrossPriceCents: number,
	) => Promise<void>
	countryCode: CountryCode
	position: number
	productSku: string
}

const UpSellProductsWrapper = ({
	upSellProducts,
	addUpSellProductToCart,
	closeUpSellDialog,
	countryCode,
	productSku,
}: UpSellProductsWrapperProps) => {
	return (
		<Container>
			<Box p={2} mt={5}>
				<Box mb={4}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<DesktopLogo
							style={{
								maxHeight: 50,
								minHeight: "100%",
								maxWidht: "90px",
								height: "50px",
								objectFit: "contain",
							}}
						/>
						<div>
							<MUIButton
								onClick={closeUpSellDialog}
								style={{
									color: "black",
									width: 30,
								}}
							>
								<Icon path={mdiClose} size={1} color="black" />
							</MUIButton>
						</div>
					</div>
				</Box>
				<Box mt={3} ml={2} mb={2} style={{ fontSize: 22 }}>
					Wird oft zusammen gekauft
				</Box>
				<div style={{ overflowY: "scroll", padding: 15, maxHeight: 600 }}>
					{upSellProducts.length > 0 ? (
						upSellProducts.map((upSellProduct, position) => {
							return (
								<UpSellProduct
									key={upSellProduct.productId}
									upSellProduct={upSellProduct}
									addUpSellProductToCart={addUpSellProductToCart}
									countryCode={countryCode}
									position={position + 1}
									productSku={productSku}
								/>
							)
						})
					) : (
						<Spinner />
					)}
				</div>
			</Box>
		</Container>
	)
}

interface UpSellProductsWrapperProps {
	upSellProducts: UpSellSellProductInterface[]
	addUpSellProductToCart: (
		upSellProduct: UpSellSellProductInterface,
		quantity: number,
		singleGrossPriceCents: number,
	) => Promise<void>
	countryCode: CountryCode
	productSku: string
	closeUpSellDialog?: () => void // close Upsells
	onClose?: () => void // close All
}

const UpSellProducts = ({ upSellProductSkus, countryCode, masterVariantSku, onClose }: Props) => {
	const { classes } = useStyles()
	const appContextState = useContext(AppContext)
	const { upSellProducts } = useUpSellProducts(
		appContextState.accessToken,
		upSellProductSkus,
		appContextState.currencyCode,
	)
	const [isOpen, setIsOpen] = useState(true)
	const [closeUpSells, setCloseUpSells] = useState(true)

	const closeDialog = () => {
		setIsOpen(false)
	}

	const closeUpSellDialog = () => {
		setCloseUpSells(false)
	}

	const addUpSellProductToCart = async (
		upSellProduct: UpSellSellProductInterface,
		quantity: number,
		singleGrossPriceCents: number,
	) => {
		const { name, productId, variantId, sku, manufacturer } = upSellProduct

		const lineItemDraft: MyLineItemDraft = {
			productId,
			variantId,
			quantity,
			custom: {
				...custom,
			},
			distributionChannel,
		}

		// TODO: Feedback
		await addLineItemsToCart(appContextState, [lineItemDraft], "de", countryCode, "EUR")

		const addToCartFieldObject: EnhancedEcommerceCartProductFieldObject = {
			name,
			id: sku,
			price: centsToEuros(singleGrossPriceCents),
			brand: manufacturer,
			category: `${masterVariantSku} - Cross Sells`,
			variant: sku,
			quantity,
		}

		trackAddToCart([addToCartFieldObject])

		// await wait(200)
		// const ga4item: GA4Item = {
		// 	item_name: name,
		// 	item_id: sku,
		// 	price: centsToEuros(singleGrossPriceCents),
		// 	item_brand: manufacturer,
		// 	item_list_name: `${masterVariantSku} - Cross Sells`,
		// 	item_variant: sku,
		// 	quantity,
		// }
		// trackAddToCartGA4([ga4item])
	}

	return (
		<>
			<>
				{/* Desktop */}

				{closeUpSells && (
					<div className={classes.desktopUpSellPosition}>
						<UpSellProductsWrapper
							upSellProducts={upSellProducts}
							addUpSellProductToCart={addUpSellProductToCart}
							countryCode={countryCode}
							productSku={masterVariantSku}
							closeUpSellDialog={closeUpSellDialog} // only close UpSells
							onClose={onClose} // close All
						/>
					</div>
				)}

				{/* Mobile */}
				<Dialog
					fullScreen
					open={isOpen}
					onClose={closeDialog}
					className={classes.mobileUpSellPosition}
				>
					<UpSellProductsWrapper
						upSellProducts={upSellProducts}
						addUpSellProductToCart={addUpSellProductToCart}
						countryCode={countryCode}
						productSku={masterVariantSku}
					/>
					<Box px={5} style={{ maxWidth: 250, marginLeft: "auto", marginRight: "auto" }}>
						<Button onClick={closeDialog} style={{ position: "absolute", bottom: 0 }}>
							Weiter zur Übersicht
						</Button>
					</Box>
				</Dialog>
			</>
		</>
	)
}

interface Props {
	upSellProductSkus: string[]
	masterVariantSku: string
	countryCode: CountryCode
	onClose?: () => void
}

export default UpSellProducts
